import { useRef, useEffect, useState } from 'react';

import Head from 'design-system/Head';
import { WithCopy } from '@scale/frontend-lib/sensor-fusion';
import { Typography } from '@scale/scaleui';
import { ISensorFusionStageDefinitionLookupDto } from '@scale/sensor-fusion-shared';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import HideableSection from 'react-scale/corp/components/UI/HideableSection';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

import { openSelectedIfUrl } from '../helpers/url';
import { DOUBLE_COLUMN_MIN_WIDTH } from '../constants';
import StageDefinitionBreakdownPanel from './StageDefinitionBreakdownPanel';
import StageDefinitionOverviewPanel from './StageDefinitionOverviewPanel';
import StageDefinitionToolsPanel from './StageDefinitionToolsPanel';
import StageDefinitionPipelinePanel from './StageDefinitionPipelinePanel';

type StageDefinitionLookupViewProps = {
  stageDefinition: ISensorFusionStageDefinitionLookupDto;
};

const StageDefinitionLookupView = ({ stageDefinition }: StageDefinitionLookupViewProps) => {
  const myRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const { isRemoAdmin } = useRemoAdminCtx();

  useEffect(() => {
    const updateWidth = () => {
      setWidth(myRef?.current?.getBoundingClientRect().width);
    };

    window.addEventListener('resize', updateWidth);
    updateWidth();
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const leftColumnElements = (
    <>
      <StageDefinitionOverviewPanel stageDefinition={stageDefinition} />
      {!isRemoAdmin && (
        <>
          <StageDefinitionToolsPanel stageDefinition={stageDefinition} />
          <HideableSection name="JSON">
            <ReactJsonView
              src={stageDefinition}
              name={false}
              theme="monokai"
              iconStyle="triangle"
              collapsed={1}
              onSelect={openSelectedIfUrl}
              collapseStringsAfterLength={120}
              groupArraysAfterLength={false}
              enableClipboard={true}
              displayObjectSize={false}
              displayDataTypes={false}
            />
          </HideableSection>
        </>
      )}
    </>
  );

  const rightColumnElements = (
    <>
      <StageDefinitionPipelinePanel stageDefinition={stageDefinition} />
      <StageDefinitionBreakdownPanel stageDefinition={stageDefinition} />
    </>
  );

  return (
    <div className="scaleui scale-tailwind scale-theme w-full flex justify-center" ref={myRef}>
      <Head title={`${stageDefinition.name} Stage on ${stageDefinition.project.name}`} />
      <div className="flex flex-col gap-4 max-w-full">
        <Typography.H1>Stage Definition</Typography.H1>
        <div>
          <span className="font-medium text-lg">{stageDefinition.name} </span>
          <WithCopy className="inline-block" copyText={stageDefinition._id}>
            {stageDefinition._id}
          </WithCopy>
        </div>
        {width && width >= DOUBLE_COLUMN_MIN_WIDTH ? (
          <div className="flex gap-4 min-w-0">
            <div className="flex flex-col gap-4 min-w-0 w-1/3 overflow-wrap-anywhere">
              {leftColumnElements}
            </div>
            <div className="flex flex-col gap-4 min-w-0 w-2/3 overflow-wrap-anywhere">
              {rightColumnElements}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 min-w-0 overflow-wrap-anywhere">
            {leftColumnElements}
            {rightColumnElements}
          </div>
        )}
        <style jsx>{`
          .overflow-wrap-anywhere {
            overflow-wrap: anywhere;
          }
        `}</style>
      </div>
    </div>
  );
};

export default StageDefinitionLookupView;
